import { FirebaseApp, getApps, initializeApp } from "firebase/app";
import {
  isSupported,
  MessagePayload,
  Messaging,
  getMessaging,
  getToken,
  onMessage,
} from "firebase/messaging";
import { Client, PushNotification } from "@twilio/conversations";
import registerServiceWorker from "./serviceWorker";

type FirebaseWebConfig = {
  apiKey?: string;
  authDomain?: string;
  projectId?: string;
  storageBucket?: string;
  messagingSenderId?: string;
  appId?: string;
  measurementId?: string;
  vapidKey?: string;
};

declare global {
  interface Window {
    firebaseConfig?: FirebaseWebConfig;
  }
}

let app: FirebaseApp | undefined;
let messaging: Messaging | undefined;
let initialized = false;
let initPromise: Promise<boolean> | undefined;
let serviceWorkerRegistration: ServiceWorkerRegistration | undefined;
let unsubscribeForegroundMessages: (() => void) | undefined;
let messagingUnavailableReason =
  "Firebase messaging has not been initialized yet.";

const getPushApiSupportReason = (): string | undefined => {
  if (!("Notification" in window)) {
    return "This browser does not expose the Notifications API.";
  }

  if (!("serviceWorker" in navigator)) {
    return "This browser does not expose service workers.";
  }

  if (!("PushManager" in window)) {
    return "This browser does not expose the Push API.";
  }

  if (!window.isSecureContext) {
    return "Push notifications require HTTPS, except on localhost.";
  }

  return undefined;
};

const hasRequiredFirebaseConfig = (
  config?: FirebaseWebConfig
): config is FirebaseWebConfig =>
  !!(
    config?.apiKey &&
    config.projectId &&
    config.messagingSenderId &&
    config.appId
  );

const getVapidKey = (): string | undefined => {
  const key =
    window.firebaseConfig?.vapidKey || process.env.REACT_APP_FIREBASE_VAPID_KEY;

  return key || undefined;
};

const initMessaging = async (): Promise<boolean> => {
  if (initialized) {
    return true;
  }

  const supportReason = getPushApiSupportReason();

  if (supportReason) {
    messagingUnavailableReason = supportReason;
    return false;
  }

  const config = window.firebaseConfig;

  if (!hasRequiredFirebaseConfig(config)) {
    messagingUnavailableReason =
      "Firebase config is missing. Make sure /firebase-config.js loads before the app bundle and defines window.firebaseConfig.";
    console.warn("Couldn't initialize Firebase messaging: missing config");
    return false;
  }

  if (!initPromise) {
    initPromise = isSupported()
      .then((supported) => {
        if (!supported) {
          messagingUnavailableReason =
            "The Firebase Messaging SDK does not support this browser context.";
          return false;
        }

        app = getApps()[0] ?? initializeApp(config);
        messaging = getMessaging(app);
        initialized = true;
        messagingUnavailableReason = "";
        return true;
      })
      .catch((error) => {
        messagingUnavailableReason =
          error instanceof Error
            ? error.message
            : "Firebase Messaging initialization threw an unknown error.";
        console.warn("Couldn't initialize Firebase messaging", error);
        return false;
      });
  }

  return initPromise;
};

const getNotificationLink = (
  data: PushNotification["data"] | MessagePayload["data"] | undefined,
  action?: string | null
): string => {
  const payloadData = data as Record<string, string> | undefined;

  return (
    payloadData?.click_action ||
    payloadData?.link ||
    payloadData?.url ||
    action ||
    "/"
  );
};

const openNotificationLink = (url: string): void => {
  try {
    window.focus();
    window.location.assign(new URL(url, window.location.origin).href);
  } catch {
    window.location.assign("/");
  }
};

const getForegroundNotificationTitle = (
  pushNotification: PushNotification
): string => {
  return (
    pushNotification.title ||
    // TODO: remove fallback when new version of sdk will be released
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore
    pushNotification.data.conversationTitle ||
    "SellBot AiML"
  );
};

const getTokenOptions = (
  registration: ServiceWorkerRegistration
): {
  serviceWorkerRegistration: ServiceWorkerRegistration;
  vapidKey?: string;
} => {
  const vapidKey = getVapidKey();
  const options = {
    serviceWorkerRegistration: registration,
  };

  return vapidKey ? { ...options, vapidKey } : options;
};

const bindForegroundMessages = (convoClient: Client): void => {
  if (!messaging) {
    return;
  }

  if (unsubscribeForegroundMessages) {
    unsubscribeForegroundMessages();
  }

  unsubscribeForegroundMessages = onMessage(messaging, (payload) => {
    console.log("FcmNotifications: push received", payload);
    convoClient.handlePushNotification(payload);
  });
};

export const initFcmServiceWorker = async (): Promise<void> => {
  if (!(await initMessaging())) {
    return;
  }

  try {
    serviceWorkerRegistration =
      (await registerServiceWorker()) ?? (await navigator.serviceWorker.ready);
    console.log(
      "ServiceWorker registered with scope:",
      serviceWorkerRegistration.scope
    );
  } catch (e) {
    console.log("ServiceWorker registration failed:", e);
  }
};

export const subscribeFcmNotifications = async (
  convoClient: Client
): Promise<boolean> => {
  if (!("Notification" in window)) {
    return false;
  }

  if (Notification.permission !== "granted") {
    console.log(
      "FcmNotifications: skipping subscription, permission is",
      Notification.permission
    );
    return false;
  }

  await initFcmServiceWorker();
  if (!messaging || !serviceWorkerRegistration) {
    console.log("FcmNotifications: can't initialize firebase messaging");
    return false;
  }

  const fcmToken = await getToken(
    messaging,
    getTokenOptions(serviceWorkerRegistration)
  );
  if (!fcmToken) {
    console.log("FcmNotifications: can't get fcm token");
    return false;
  }

  console.log("FcmNotifications: got fcm token", fcmToken);
  await convoClient.setPushRegistrationId("fcm", fcmToken);
  bindForegroundMessages(convoClient);

  return true;
};

export const ensureFcmNotificationsEnabled = async (
  convoClient: Client
): Promise<NotificationPermission | "unsupported"> => {
  if (!("Notification" in window)) {
    globalThis.alert("Push notifications are not supported in this browser.");
    return "unsupported";
  }

  const supportReason = getPushApiSupportReason();

  if (supportReason) {
    globalThis.alert(
      `Push notifications are not available in this browser context. ${supportReason}`
    );
    return "unsupported";
  }

  if (!(await initMessaging())) {
    globalThis.alert(
      `Push notifications are not configured yet because Firebase messaging is unavailable. ${messagingUnavailableReason}`
    );
    return "unsupported";
  }

  if (Notification.permission === "denied") {
    globalThis.alert(
      "Notifications are blocked for this app. Open your browser or device site settings and allow notifications for SellBot AiML."
    );
    return "denied";
  }

  const permission =
    Notification.permission === "granted"
      ? "granted"
      : await Notification.requestPermission();

  if (permission === "granted") {
    await subscribeFcmNotifications(convoClient);
  }

  return permission;
};

export const showNotification = (pushNotification: PushNotification): void => {
  if (!initialized || !("Notification" in window)) {
    return;
  }

  const notificationOptions = {
    body: pushNotification.body ?? "",
    icon: "/sellbotaiml-icon-192.png",
    data: {
      url: getNotificationLink(pushNotification.data, pushNotification.action),
    },
  };

  const notification = new Notification(
    getForegroundNotificationTitle(pushNotification),
    notificationOptions
  );
  notification.onclick = (event) => {
    console.log("notification.onclick", event);
    event.preventDefault(); // prevent the browser from focusing the Notification's tab
    openNotificationLink(notificationOptions.data.url);
    notification.close();
  };
};
